import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const strict = { additionalProperties: false } as const;

export const SUPPORT_ACCESS_DURATION_MINUTES = [15, 30, 60, 120] as const;

export const SupportAccessGrantStatusSchema = Type.Union([
  Type.Literal("ACTIVE"),
  Type.Literal("REVOKED"),
  Type.Literal("EXPIRED"),
]);
export type SupportAccessGrantStatus = Static<typeof SupportAccessGrantStatusSchema>;

export const SupportAccessGrantReadModelSchema = Type.Object(
  {
    grantId: Type.String({ minLength: 1, maxLength: 96 }),
    storeId: Type.String({ minLength: 1, maxLength: 96 }),
    supportDisplayName: Type.String({ minLength: 1, maxLength: 40 }),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    capability: Type.Literal("DIAGNOSTIC_READ_REDACTED"),
    status: SupportAccessGrantStatusSchema,
    version: Type.Integer({ minimum: 1 }),
    validFrom: Type.String({ format: "date-time" }),
    validUntil: Type.String({ format: "date-time" }),
    createdBy: Type.String({ minLength: 1, maxLength: 96 }),
    createdAt: Type.String({ format: "date-time" }),
    revokedBy: Type.Union([Type.String({ minLength: 1, maxLength: 96 }), Type.Null()]),
    revokedReason: Type.Union([Type.String({ minLength: 2, maxLength: 200 }), Type.Null()]),
    revokedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
  },
  strict,
);
export type SupportAccessGrantReadModel = Static<typeof SupportAccessGrantReadModelSchema>;

export const SupportAccessWorkspaceSchema = Type.Object(
  {
    generatedAt: Type.String({ format: "date-time" }),
    grants: Type.Array(SupportAccessGrantReadModelSchema, { maxItems: 200 }),
  },
  strict,
);
export type SupportAccessWorkspace = Static<typeof SupportAccessWorkspaceSchema>;

export const CreateSupportAccessGrantRequestSchema = Type.Object(
  {
    supportDisplayName: Type.String({ minLength: 1, maxLength: 40 }),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    durationMinutes: Type.Union(
      SUPPORT_ACCESS_DURATION_MINUTES.map((value) => Type.Literal(value)),
    ),
    accessToken: Type.String({ pattern: "^bsp_[A-Za-z0-9_-]{43}$" }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  strict,
);
export type CreateSupportAccessGrantRequest = Static<typeof CreateSupportAccessGrantRequestSchema>;

export const RevokeSupportAccessGrantRequestSchema = Type.Object(
  {
    expectedVersion: Type.Integer({ minimum: 1 }),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  strict,
);
export type RevokeSupportAccessGrantRequest = Static<typeof RevokeSupportAccessGrantRequestSchema>;

export const SupportAccessMutationResultSchema = Type.Object(
  {
    operation: Type.Union([Type.Literal("CREATE"), Type.Literal("REVOKE")]),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
    accessTokenFingerprint: Type.String({ pattern: "^[a-f0-9]{12}$" }),
    grant: SupportAccessGrantReadModelSchema,
  },
  strict,
);
export type SupportAccessMutationResult = Static<typeof SupportAccessMutationResultSchema>;

export function isSupportAccessWorkspace(value: unknown): value is SupportAccessWorkspace {
  return Value.Check(SupportAccessWorkspaceSchema, value);
}

export function isCreateSupportAccessGrantRequest(
  value: unknown,
): value is CreateSupportAccessGrantRequest {
  return Value.Check(CreateSupportAccessGrantRequestSchema, value);
}

export function isRevokeSupportAccessGrantRequest(
  value: unknown,
): value is RevokeSupportAccessGrantRequest {
  return Value.Check(RevokeSupportAccessGrantRequestSchema, value);
}

export function isSupportAccessMutationResult(
  value: unknown,
): value is SupportAccessMutationResult {
  return Value.Check(SupportAccessMutationResultSchema, value);
}
